import {
  AG_GRID_COLUMN_AGGREGATION_FUNC,
  COLUMN_FORMATTING_TYPE,
  DATE_FORMAT,
} from '@/constants/common.constant'
import {
  AgGridCellValueMethod,
  ColumnAggMethod,
} from '@/enums/axiom-report.enum'
import { IGfpaDetailsDTO } from '@/interfaces/gfpaReport.interface'
import { IGfrDetailsDTO } from '@/interfaces/gfrReport.interface'
import { IRvAttributionBySectorDTO } from '@/interfaces/rvReport.interface'
import {
  ColDef,
  ColGroupDef,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { IRvSecurityDetailsData } from '@interfaces/rvDetails.interface'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { isNull, isUndefined } from 'lodash'
import { getColumnDetail } from './grid-utils/columnUtils'
import { formatNumber, removeCommaFromString } from './utils'
dayjs.extend(utc)

export const gridDateFormatter = (
  params: ValueFormatterParams | ITooltipParams,
  format?: string,
) => {
  const dateFormat = format || DATE_FORMAT.TABLE_CHART
  const { value } = params
  if (value === 'Total') {
    return value
  }
  const convertedDate = dayjs(String(value))
  return convertedDate.isValid() ? convertedDate.format(dateFormat) : ''
}

export const gridDateTimeFormatter = (
  params: ValueFormatterParams | ITooltipParams,
  format?: string,
) => {
  const dateFormat = format || `${DATE_FORMAT.TABLE_CHART} hh:mm a`
  return params?.value && dayjs(params?.value).isValid()
    ? dayjs(params.value).format(dateFormat)
    : ''
}

/**
 * Compares Dates by time at Midnight of both dates
 * @param filterDate Date as Input in Filter
 * @param cellValue DateString as in Original Cell Value
 * @returns 0: Equal | 1: After | -1: Before
 */
export const customDateComparator = (
  filterDate: Date,
  cellValue: string,
): number => {
  // Check for empty or invalid cellValue
  if (!cellValue || !dayjs(cellValue).isValid()) {
    return -1 // Treat empty or invalid as less than any date
  }

  // Create a dayjs object from cellValue
  const cellDate = dayjs(cellValue).utc().startOf('day') // Normalize to start of the day

  // Create a dayjs object for filterDate normalized to start of the day
  const filterDateDayjs = dayjs(filterDate).utc().startOf('day')

  // Compare the two dates
  if (cellDate.isBefore(filterDateDayjs)) {
    return -1 // cellDate is before filterDate
  } else if (cellDate.isAfter(filterDateDayjs)) {
    return 1 // cellDate is after filterDate
  } else {
    return 0 // Dates are equal
  }
}

export const getUpdatedColumnsWithTooltip = (
  columnsList: ColDef[],
  reportType?: string,
) => {
  return columnsList.map((col: ColDef) => {
    let columnObj = {
      headerTooltip: col.headerName,
      tooltipField: col.field,
      filter: true,
      ...col,
    }
    if (reportType) {
      // Get Column meta Data
      const columnMetaData = getColumnDetail(
        columnObj.field as string,
        reportType,
      )

      if (columnMetaData && Object.keys(columnMetaData).length) {
        const unit = COLUMN_FORMATTING_TYPE[columnMetaData.format.toLowerCase()]

        columnObj = {
          ...columnObj,
          headerName: columnMetaData.header,
          headerTooltip: columnMetaData.header,
        }

        if (columnMetaData.agg_method !== ColumnAggMethod.NONE) {
          columnObj.aggFunc = String(
            AG_GRID_COLUMN_AGGREGATION_FUNC[
              columnMetaData.agg_method.toLowerCase()
            ],
          )
        }
        if (!isNull(unit) && !isUndefined(unit)) {
          columnObj.cellStyle = {
            textAlign: 'right',
          }
          columnObj.valueFormatter = (params) => {
            if (!params.value && params.node?.level === -1) return ''
            return formatCellValue(
              formatNumber(params.value),
              columnMetaData.format,
              columnMetaData.header,
              columnMetaData.name,
            )
          }
        } else if (columnMetaData.format) {
          columnObj.valueFormatter = (params) =>
            formatCellValue(params.value, columnMetaData.format)
        } else {
          columnObj.valueFormatter = (params) =>
            formatCellValue(params.value, '')
        }

        columnObj.tooltipField = undefined
        columnObj.tooltipValueGetter = (params) => {
          let tooltipValue = params.value
          if (
            tooltipValue &&
            (typeof tooltipValue === 'number' ||
              typeof tooltipValue === 'object')
          ) {
            return formatCellValue(tooltipValue, columnMetaData.format)
          }
          return tooltipValue
        }
      }
    }
    return columnObj
  })
}

export const getColumnsChildrenWithTooltip = (
  columnsList: ColDef[],
): (ColDef | ColGroupDef)[] => {
  return columnsList.map((col: ColDef) => {
    if (col.rowGroup) {
      return col
    } else {
      return {
        ...col,
        headerTooltip: col.headerName,
        children: getUpdatedChildren((col as ColGroupDef).children),
      }
    }
  })
}

export const getUpdatedChildren = (
  childrenColumns: ColDef[],
): (ColDef | ColGroupDef)[] => {
  return childrenColumns.map((col: ColDef) => {
    return {
      ...col,
      headerTooltip: col.headerName,
      tooltipField: col.field,
    }
  })
}

export const getTopAndBottomFiveSecurities = (
  dataList: IGfpaDetailsDTO[] | IGfrDetailsDTO[] | IRvSecurityDetailsData[],
  sortByKey: string,
): {
  topFiveSecurities:
    | IGfpaDetailsDTO[]
    | IGfrDetailsDTO[]
    | IRvSecurityDetailsData[]
  bottomFiveSecurities:
    | IGfpaDetailsDTO[]
    | IGfrDetailsDTO[]
    | IRvSecurityDetailsData[]
} => {
  const sortedList = [...dataList].sort(
    (a: any, b: any) => b[sortByKey] - a[sortByKey], // descending order
  )
  const topFiveSecurities = sortedList.slice(
    0,
    Math.min(5, sortedList.length),
  ) as IGfpaDetailsDTO[] | IGfrDetailsDTO[] | IRvSecurityDetailsData[]
  let bottomFiveSecurities:
    | IGfpaDetailsDTO[]
    | IGfrDetailsDTO[]
    | IRvSecurityDetailsData[] = []
  if (dataList.length > 5) {
    bottomFiveSecurities = sortedList.slice(-Math.min(5, sortedList.length)) as
      | IGfpaDetailsDTO[]
      | IGfrDetailsDTO[]
      | IRvSecurityDetailsData[]
  }
  return { topFiveSecurities, bottomFiveSecurities }
}

export const getTop11AttributionBySector = (
  attributionBySectorList: IRvAttributionBySectorDTO[],
  sortByKey: string,
): IRvAttributionBySectorDTO[] => {
  const sortedDataList = [...attributionBySectorList].sort(
    (a: any, b: any) => (b[sortByKey] || 0) - (a[sortByKey] || 0),
  )
  const top11LargestBaseReturnContribution = sortedDataList.slice(
    0,
    Math.min(11, sortedDataList.length),
  )

  const remainingItems = sortedDataList.slice(11)
  const otherAttributionBySectorItems = remainingItems.reduce(
    (acc, item) => {
      acc.port_weight += item.port_weight || 0
      acc.bench_weight += item.bench_weight || 0
      acc.active_weight += item.active_weight || 0
      acc.port_total_return += item.port_total_return || 0
      acc.bench_total_return += item.bench_total_return || 0
      acc.active_return += item.active_return || 0
      acc.allocation_low1 += item.allocation_low1 || 0
      acc.selection_effect1 += item.selection_effect1 || 0
      acc.active_currency_return1 += item.active_currency_return1 || 0
      acc.port_total_return_contrib += item.port_total_return_contrib || 0
      acc.bench_total_return_contrib += item.bench_total_return_contrib || 0
      return acc
    },
    {
      group1: 'Other',
      port_weight: 0,
      bench_weight: 0,
      active_weight: 0,
      port_total_return: 0,
      bench_total_return: 0,
      active_return: 0,
      allocation_low1: 0,
      selection_effect1: 0,
      active_currency_return1: 0,
      port_total_return_contrib: 0,
      bench_total_return_contrib: 0,
    },
  )
  top11LargestBaseReturnContribution.push(otherAttributionBySectorItems)
  return top11LargestBaseReturnContribution
}

export const formatCellValue = (
  value: any,
  format: string,
  header?: string,
  fieldName?: string,
) => {
  const unit = COLUMN_FORMATTING_TYPE[format?.toLowerCase()]
  if (isNull(value) || isUndefined(value)) {
    return ''
  } else if (value === 'N/A') {
    return value
  } else if (format === 'Percentage') {
    if (header?.includes('%')) {
      return value
    } else {
      return `${value}${unit}`
    }
  } else if (format === 'Basis Points') {
    return value
  } else if (format === 'Date') {
    if (value === 'Total') {
      return value
    }

    return value && dayjs(value).isValid()
      ? dayjs(value).format(DATE_FORMAT.TABLE_CHART)
      : ''
  }
  return value
}

export const getCellValue = (
  rowData: any,
  field: string,
  type: string,
  minFractionDigits?: number,
  maxFractionDigits?: number,
) => {
  if (rowData) {
    if (rowData[field]) {
      let value = formatNumber(
        rowData[field],
        false,
        minFractionDigits,
        maxFractionDigits,
      )
      if (
        value &&
        String(value).includes(',') &&
        type === AgGridCellValueMethod.VALUE_FORMATTER
      ) {
        return value
      }
      value = removeCommaFromString(String(value)) // to handle Ag Grid column filter on number value
      return Number(value)
    }
    return rowData[field]
  }
  return rowData
}
